import './styles/global.css';
import 'lenis/dist/lenis.css';
import { createAudio } from './shared/audio';
import { createMotionScope, createScroll, ScrollTrigger } from './shared/motion';
import { pageLoaders, resolveRoute } from './shared/navigation';
import { readPreferences, savePreferences } from './shared/preferences';
import type { Cleanup, Route, SceneControls } from './shared/contracts';

const app = document.querySelector<HTMLDivElement>('#app')!;
const preferences = readPreferences();
const audio = createAudio();
audio.setMuted(!preferences.sound);
audio.setVolume(preferences.volume);
app.innerHTML = `<header class="masthead"><a class="wordmark" href="/" data-route aria-label="CHAOSVERSE home">CHAOS<span>VERSE</span><b aria-hidden="true">✳</b></a>
  <nav aria-label="Universes"><a href="/" data-route>The Snap</a><a href="/rift" data-route>The Rift</a><a href="/chaos" data-route>The Chaos</a></nav></header>
  <main id="main" tabindex="-1"></main>
  <aside class="reality-remote" aria-label="Reality Remote"><span class="remote-name">Reality Remote</span>
    <button type="button" id="sound" aria-pressed="false">Sound off</button>
    <label class="volume-label">Volume <input id="volume" type="range" min="0" max="1" step="0.05" aria-label="Volume"></label>
    <label>Quality <select id="quality"><option value="auto">Auto</option><option value="cinematic">Cinematic</option><option value="lightweight">Lightweight preview</option></select></label>
    <button type="button" id="cinematic" aria-pressed="false">Cinematic mode</button>
    <button type="button" id="directors" aria-pressed="false">Director's Cut</button>
    <button type="button" id="replay" disabled>Replay scene</button><button type="button" id="skip" disabled>Skip scene</button>
  </aside><p class="sr-only" id="announcement" role="status" aria-live="polite"></p>`;
const host = document.querySelector<HTMLElement>('#main')!;
const announce = (message: string) => { document.querySelector('#announcement')!.textContent = message; };
let currentCleanup: Cleanup = () => {};
let controller = new AbortController();
let stopScroll: Cleanup = () => {};
let activeRoute: Route = '/';
let controls: SceneControls | undefined;
let revision = 0;

const sound = document.querySelector<HTMLButtonElement>('#sound')!;
const cinematic = document.querySelector<HTMLButtonElement>('#cinematic')!;
const directors = document.querySelector<HTMLButtonElement>('#directors')!;
const replay = document.querySelector<HTMLButtonElement>('#replay')!;
const skip = document.querySelector<HTMLButtonElement>('#skip')!;
const quality = document.querySelector<HTMLSelectElement>('#quality')!;
const volume = document.querySelector<HTMLInputElement>('#volume')!;

function reflectPreferences() {
  sound.textContent = preferences.sound ? 'Sound on' : 'Sound off';
  sound.setAttribute('aria-pressed', String(preferences.sound));
  cinematic.setAttribute('aria-pressed', String(preferences.cinematic));
  directors.setAttribute('aria-pressed', String(preferences.directorsCut));
  document.body.classList.toggle('cinematic', preferences.cinematic);
  quality.value = preferences.quality;
  volume.value = String(preferences.volume);
  savePreferences(preferences);
}

async function render(route: Route, focus: boolean) {
  const request = ++revision;
  controller.abort(); currentCleanup(); stopScroll();
  controller = new AbortController();
  const signal = controller.signal;
  controls = undefined; replay.disabled = true; skip.disabled = true;
  activeRoute = route;
  host.innerHTML = '<p class="loading" role="status">Opening universe…</p>';
  try {
    const module = await pageLoaders[route]();
    if (request !== revision) return;
    host.replaceChildren();
    const motion = createMotionScope(host);
    const cleanup = await module.default.mount({
      host, signal, audio, motion, preferences, navigate, announce,
      registerSceneControls(next) {
        if (signal.aborted) return () => {};
        controls = next; replay.disabled = false; skip.disabled = false;
        next.setDirectorsCut(preferences.directorsCut);
        return () => { if (controls === next) { controls = undefined; replay.disabled = true; skip.disabled = true; } };
      },
    });
    if (request !== revision) { cleanup(); motion.dispose(); return; }
    currentCleanup = () => { cleanup(); motion.dispose(); };
    stopScroll = createScroll(preferences.quality);
    ScrollTrigger.refresh();
    document.title = `CHAOSVERSE — ${route === '/' ? 'The Snap' : route === '/rift' ? 'The Rift' : 'The Chaos'}`;
    document.querySelectorAll<HTMLAnchorElement>('nav [data-route]').forEach(link => {
      if (link.getAttribute('href') === route) link.setAttribute('aria-current', 'page');
      else link.removeAttribute('aria-current');
    });
    window.scrollTo(0, 0);
    if (focus) host.focus({ preventScroll: true });
  } catch (error) {
    if (request !== revision) return;
    console.error(error);
    host.innerHTML = '<section class="chapter"><h1>Universe unavailable.</h1><p>Reload to retry, or return to <a href="/">The Snap</a>.</p></section>';
    announce('The universe could not be loaded.');
  }
}

async function navigate(route: Route) {
  if (location.pathname !== route) history.pushState({}, '', route);
  await render(route, true);
}

document.addEventListener('click', event => {
  if (!(event.target instanceof Element) || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target.closest<HTMLAnchorElement>('a[data-route]');
  if (!link || link.target === '_blank') return;
  const route = resolveRoute(new URL(link.href).pathname);
  if (!route) return;
  event.preventDefault(); void navigate(route);
});

window.addEventListener('popstate', () => { void showLocation(true); });
function showLocation(focus: boolean) {
  const route = resolveRoute(location.pathname);
  if (route) return render(route, focus);
  ++revision; controller.abort(); currentCleanup(); stopScroll();
  controls = undefined; replay.disabled = true; skip.disabled = true;
  host.innerHTML = '<section class="chapter"><h1>Outside the timeline.</h1><p>This universe does not exist.</p><a href="/" data-route>Return to The Snap</a></section>';
  document.title = 'CHAOSVERSE — Unknown universe';
  if (focus) host.focus();
  return Promise.resolve();
}

sound.addEventListener('click', async () => {
  try {
    await audio.unlock(); preferences.sound = !preferences.sound;
    audio.setMuted(!preferences.sound); reflectPreferences();
    if (preferences.sound) audio.cue('initialize');
  } catch { announce('Audio could not start. Try enabling sound again.'); }
});
volume.addEventListener('input', () => { preferences.volume = Number(volume.value); audio.setVolume(preferences.volume); savePreferences(preferences); });
quality.addEventListener('change', () => { preferences.quality = quality.value as typeof preferences.quality; reflectPreferences(); void render(activeRoute, false); });
cinematic.addEventListener('click', () => { preferences.cinematic = !preferences.cinematic; reflectPreferences(); });
directors.addEventListener('click', () => {
  if (!controls) { announce("Director's Cut will be available when cinematic scenes are implemented."); return; }
  preferences.directorsCut = !preferences.directorsCut; controls.setDirectorsCut(preferences.directorsCut); reflectPreferences();
});
replay.addEventListener('click', () => controls?.replay());
skip.addEventListener('click', () => controls?.skip());
reflectPreferences();
void showLocation(false);
window.addEventListener('pagehide', () => { controller.abort(); currentCleanup(); stopScroll(); audio.dispose(); }, { once: true });
